import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MathRender from "@/components/Math";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Lightbulb,
  RotateCcw,
  Sparkles,
  XCircle,
} from "lucide-react";

// ---- helpers (kept in-file to avoid changing shared modules) -------------

function normaliseAnswer(text) {
  if (!text) return "";
  return String(text)
    .replace(/\$/g, "")
    .replace(/\\\s*/g, "")
    .replace(/\s+/g, "")
    .replace(/[.,;!?)(]$/g, "")
    .toLowerCase();
}

function answersMatch(userAns, correctAns) {
  const u = normaliseAnswer(userAns);
  const c = normaliseAnswer(correctAns);
  if (!u || !c) return false;
  if (u === c) return true;
  const uNum = u.match(/-?[0-9]+(\.[0-9]+)?/g);
  const cNum = c.match(/-?[0-9]+(\.[0-9]+)?/g);
  if (uNum && cNum && uNum.length === 1 && cNum.length === 1) {
    return parseFloat(uNum[0]) === parseFloat(cNum[0]);
  }
  return false;
}

function classLevelFrom(courseId) {
  const m = courseId?.match(/class-(\d+)/);
  return m ? parseInt(m[1], 10) : 10;
}

// ---- sub-components -------------------------------------------------------

function HintRung({ hint }) {
  const kindLabel = {
    nudge: "Nudge",
    method: "Method",
    worked: "Worked step",
    full: "Full solution",
  }[hint.kind] || `Hint ${hint.level}`;
  return (
    <div
      className="fade-up rounded-lg border border-yellow-300/25 bg-yellow-300/[0.04] p-3 text-sm text-zinc-200"
      data-testid={`topic-hint-${hint.level}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-yellow-300 mb-1.5">
        <Lightbulb className="w-3 h-3" />
        {kindLabel} · Hint {hint.level}/4
      </div>
      <div className="text-zinc-100 leading-relaxed">
        <MathRender>{hint.text}</MathRender>
      </div>
    </div>
  );
}

function AIHelp({ question, userAnswer, correctAnswer, classLevel, chapterHint }) {
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/practice-help", {
        question,
        user_answer: userAnswer,
        correct_answer: correctAnswer,
        class_level: classLevel,
        chapter_hint: chapterHint,
      })
      .then((r) => !cancelled && setAnswer(r.data.answer || ""))
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "AI Tutor is unavailable right now. Try again shortly.");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, userAnswer]);

  if (loading) {
    return (
      <div className="fade-up rounded-lg border border-pink-300/25 bg-pink-300/[0.04] p-4 text-sm text-zinc-300" data-testid="topic-ai-loading">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-pink-300 mb-2">
          <Sparkles className="w-3 h-3 animate-pulse" /> AI Tutor is diagnosing your answer…
        </div>
        <div className="animate-pulse text-zinc-500">Analysing the misconception…</div>
      </div>
    );
  }
  if (error) {
    return <div className="rounded-lg border border-red-300/30 bg-red-300/5 p-3 text-sm text-red-200" data-testid="topic-ai-error">{error}</div>;
  }
  return (
    <div className="fade-up rounded-lg border border-pink-300/30 bg-gradient-to-br from-pink-300/[0.06] to-transparent p-4" data-testid="topic-ai-response">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-pink-300 mb-2">
        <Sparkles className="w-3 h-3" /> AI Tutor · Misconception coach
      </div>
      <div className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">
        <MathRender>{answer}</MathRender>
      </div>
    </div>
  );
}

// ---- main page -----------------------------------------------------------

export default function TopicPractice() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [state, setState] = useState("input");
  const [hintsShown, setHintsShown] = useState(0);
  const [askedAI, setAskedAI] = useState(false);
  const [tally, setTally] = useState({ correct: 0, wrong: 0, skipped: 0 });
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    setLoading(true);
    api.get(`/learn/topics/${slug}/practice?limit=15`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const questions = data?.questions || [];
  const q = questions[idx];
  const total = questions.length;
  const classLevel = classLevelFrom(q?.course_id);
  const chapterHint = q?.unit_title;
  const isDone = idx >= total && total > 0;

  useEffect(() => {
    setAnswer("");
    setState("input");
    setHintsShown(0);
    setAskedAI(false);
    startedAtRef.current = Date.now();
  }, [idx]);

  const handleCheck = () => {
    if (!q || !answer.trim()) return;
    if (answersMatch(answer, q.answer_text)) {
      setState("correct");
      setTally((t) => ({ ...t, correct: t.correct + 1 }));
    } else {
      setState("wrong");
      setHintsShown((h) => Math.max(h, 1));
    }
  };
  const revealNextHint = () => setHintsShown((h) => Math.min(h + 1, 4));
  const handleSkip = () => {
    setTally((t) => ({ ...t, skipped: t.skipped + 1 }));
    setIdx((i) => i + 1);
  };
  const handleNext = () => {
    if (state === "wrong") setTally((t) => ({ ...t, wrong: t.wrong + 1 }));
    setIdx((i) => i + 1);
  };
  const restart = () => {
    setIdx(0);
    setTally({ correct: 0, wrong: 0, skipped: 0 });
  };
  const progressPct = useMemo(() => (total ? Math.round((idx / total) * 100) : 0), [idx, total]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="topic-practice-loading">Loading topic practice…</div>;
  if (!data) return <div className="text-red-300 text-sm">Topic not found.</div>;

  if (!data.practice_bank_ready || total === 0) {
    return (
      <div data-testid="topic-practice-empty" className="max-w-2xl">
        <Link to={`/topics/${slug}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to topic
        </Link>
        <div className="rounded-lg border border-orange-300/25 bg-orange-300/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-300 mb-1.5 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Practice bank in progress
          </div>
          <div className="text-sm text-zinc-200">
            Cross-grade practice for this topic unlocks once we finish seeding questions across every unit.
          </div>
        </div>
      </div>
    );
  }

  if (isDone) {
    const accuracy = total ? Math.round((tally.correct / total) * 100) : 0;
    return (
      <div data-testid="topic-practice-summary" className="max-w-2xl">
        <Link to={`/topics/${slug}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to topic
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 mb-1">Topic paper complete</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">
          {accuracy}% on {data.topic.title}
        </h1>
        <p className="text-zinc-400 text-sm mb-6">
          {tally.correct} correct · {tally.wrong} wrong · {tally.skipped} skipped across {data.total_units} units.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={restart}
            data-testid="topic-practice-restart"
            className="rounded-full border border-yellow-300/40 bg-yellow-300/10 hover:bg-yellow-300/20 text-yellow-300 text-sm font-semibold px-4 py-2 flex items-center gap-1.5"
          >
            <RotateCcw className="w-4 h-4" /> Get a fresh mix
          </button>
          <button
            onClick={() => navigate(`/topics/${slug}`)}
            data-testid="topic-practice-back-detail"
            className="rounded-full border border-white/15 hover:border-white/30 text-zinc-200 text-sm font-semibold px-4 py-2"
          >
            Explore units in this topic
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="topic-practice-page" className="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <Link
          to={`/topics/${slug}`}
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300"
          data-testid="topic-practice-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {data.topic.title}
        </Link>
        <div className="text-[11px] font-mono text-zinc-500">
          Q {idx + 1} / {total}
        </div>
      </div>

      <div className="mb-6">
        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-pink-300 via-orange-300 to-yellow-300 transition-[width] duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Per-question class context — the killer feature for cross-grade practice */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
        <span className="text-pink-300">{data.topic.title}</span>
        <span className="text-zinc-700">·</span>
        <span>Class {classLevel} · Unit {q.unit_number}: {q.unit_title}</span>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#152040] p-5 mb-4" data-testid="topic-practice-stem">
        <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Problem</div>
        <div className="text-base text-zinc-100 leading-relaxed">
          <MathRender>{q.stem}</MathRender>
        </div>
      </div>

      {state !== "correct" ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 mb-4">
          <label htmlFor="topic-practice-answer" className="block text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1.5">
            Your answer
          </label>
          <div className="flex gap-2">
            <input
              id="topic-practice-answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCheck()}
              data-testid="topic-practice-answer-input"
              placeholder="Type your final answer…"
              className="flex-1 bg-transparent border border-white/10 focus:border-yellow-300/60 outline-none rounded-lg px-3 py-2 text-sm text-zinc-100"
              autoComplete="off"
            />
            <button
              onClick={handleCheck}
              disabled={!answer.trim()}
              data-testid="topic-practice-check"
              className="rounded-lg bg-yellow-300 hover:bg-yellow-200 disabled:opacity-40 disabled:cursor-not-allowed text-[#0b1226] text-sm font-semibold px-4 py-2"
            >
              Check
            </button>
          </div>
          {state === "wrong" && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-300" data-testid="topic-practice-wrong-banner">
              <XCircle className="w-4 h-4" /> Not quite — hint below.
            </div>
          )}
          <button
            onClick={handleSkip}
            data-testid="topic-practice-skip"
            className="mt-3 text-[11px] text-zinc-500 hover:text-zinc-300"
          >
            Skip this question →
          </button>
        </div>
      ) : (
        <div
          className="rounded-lg border border-emerald-300/40 bg-emerald-300/10 p-4 mb-4 flex items-start gap-3"
          data-testid="topic-practice-correct-banner"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-emerald-200 font-semibold mb-0.5">Correct!</div>
            <div className="text-xs text-zinc-400">
              You used {hintsShown} hint{hintsShown === 1 ? "" : "s"}
              {askedAI ? " and the AI Tutor" : ""}.
            </div>
          </div>
          <button
            onClick={handleNext}
            data-testid="topic-practice-next-correct"
            className="rounded-full border border-emerald-300/40 bg-emerald-300/10 hover:bg-emerald-300/20 text-emerald-200 text-xs font-semibold px-3 py-1.5 flex items-center gap-1"
          >
            Next <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {state !== "correct" && hintsShown > 0 && (
        <div className="space-y-2 mb-4">
          {q.hint_ladder.slice(0, hintsShown).map((h) => (
            <HintRung key={h.level} hint={h} />
          ))}
          {hintsShown < 4 && (
            <button
              onClick={revealNextHint}
              data-testid="topic-practice-more-hint"
              className="text-xs text-yellow-300 hover:text-yellow-200 font-semibold flex items-center gap-1"
            >
              Show next hint <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {state === "wrong" && (
        <div className="mb-4">
          {!askedAI ? (
            <button
              onClick={() => setAskedAI(true)}
              disabled={!user}
              title={!user ? "Sign in to chat with the AI Tutor" : ""}
              data-testid="topic-practice-ask-ai"
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-pink-300/30 bg-pink-300/5 hover:bg-pink-300/10 disabled:opacity-40 disabled:cursor-not-allowed p-3 text-left"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-pink-300" />
                <div>
                  <div className="text-sm text-zinc-100 font-semibold">Ask the AI Tutor about your answer</div>
                  <div className="text-[11px] text-zinc-500">
                    {user ? "It'll diagnose your specific mistake." : "Sign in to unlock personalised feedback."}
                  </div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-pink-300" />
            </button>
          ) : (
            <AIHelp
              question={q.stem}
              userAnswer={answer || "(blank)"}
              correctAnswer={q.answer_text}
              classLevel={classLevel}
              chapterHint={chapterHint}
            />
          )}
        </div>
      )}

      {state === "wrong" && (hintsShown === 4 || askedAI) && (
        <button
          onClick={handleNext}
          data-testid="topic-practice-next-wrong"
          className="w-full rounded-lg border border-white/15 hover:border-white/30 text-zinc-200 text-sm font-semibold px-4 py-2.5 flex items-center justify-center gap-1.5"
        >
          Got it — next question <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
